<script lang="ts">
  import { onMount } from "svelte"
  import { xhr, toast } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import { AlignLeft } from "lucide-svelte"
  import PrevNextSkipButtons from "$app/Pages/Plans/Onboarding/components/PrevNextSkipButtons.svelte"
  import Goal from "./components/Goal.svelte"
  import Tiptap from "$app/components/Tiptap.svelte"
  import { onlyTextButtons } from "$app/components/Tiptap/buttons"
  import { onboardingStore } from "./onboarding.svelte"
  import { appStore } from "$app/store.svelte"
  import Navigation from "./Navigation.svelte"

  import bg from "@images/onboarding/bg-eclipse.jpg"
  import img from "@images/onboarding/annual_goal.png"

  onboardingStore.completed = true

  let loaded = false

  let long_term_goal: GoalType = {} as GoalType

  async function getGoals(): Promise<void> {
    try {
      const resp = await xhr.get(
        `/plan_onboarding/${appStore.plan.id}/get_goals.json?goal_type=annual_goal`
      )
      long_term_goal = resp.data.long_term_goal

      form.id = resp.data.annual_goal.id
      form.name = resp.data.annual_goal.name
      form.assignee_id = resp.data.annual_goal.assignee_id
      form.parent_goal_id = resp.data.annual_goal.parent_goal_id
      form.description = resp.data.annual_goal.description
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading your annual goal", "error")
    } finally {
      loaded = true
    }
  }

  let errors: any = {}

  const initialForm = {
    id: null,
    name: "",
    type: "AnnualGoal",
    assignee_id: 0,
    description: "",
    parent_goal_id: null,
    parent_goal_type: "LongTermGoal"
  }

  let form = { ...initialForm }

  function validGoal(): boolean {
    errors = {}
    if (!form.name || form.name.trim().length < 3) {
      errors.name = "Enter a goal name with at least 3 characters"
    }
    if (!form.assignee_id) {
      errors.assignee_id = "You must select an assignee"
    }

    return Object.keys(errors).length === 0
  }

  async function nextStep(): Promise<void> {
    errors = {}
    if (!validGoal()) return
    onboardingStore.saving = true

    try {
      const resp = await xhr.post(
        `/plan_onboarding/${appStore.plan.id}/create_goal.json`,
        {
          goal: form,
          plan: { onboarding_step: "next_step" }
        }
      )

      appStore.plan.onboarding_steps = resp.data.plan.onboarding_steps
    } catch (e) {
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      onboardingStore.saving = false
    }
  }

  onMount(() => {
    getGoals()
    onboardingStore.setBrowserHistory({}, "annual_goal")
  })
</script>

<div class="mx-auto px-2 lg:px-6 pt-4 w-full pb-8 lg:pb-20 lg:text-left">
  <div class="px-4 w-full">
    <Navigation step="annual_goal" on:updateStep />
  </div>
  <div class="px-4 w-full lg:w-1/2 xl:pr-16">
    <h2 class="mt-12 text-5xl font-bold text-left text-white">
      Add an Annual Goal
    </h2>

    <p class="mt-6 text-lg text-white">
      Annual Goals are realistic 1-year goals. Generally there are 5 - 7 annual
      goals each year. Each annual goal you create is connected to a Long-Term
      Goal.
    </p>
    {#if loaded}
      <div class="flex items-center pt-6">
        <p class="text-2xl text-white">
          You can see the heriarchy of your goals below.
        </p>
      </div>

      <div class="scale-75">
        <ul class="hierarchy" style="transform: translateX(-100px)">
          <li>
            <Goal name={long_term_goal.name} goalType="long_term_goal" />
            <ul>
              <li>
                <Goal name={form.name} goalType="annual_goal" />
              </li>
            </ul>
          </li>
        </ul>
      </div>

      {#if form.parent_goal_id !== null}
        <div class="space-y-3 mt-2 max-w-xl">
          <div>
            <div class="mt-6 grid grid-cols-2 gap-y-3 gap-x-4">
              <div class="col-span-2">
                <label for="name" class="text-white text-lg">Name </label>
                <div class="mt-2 input-cont" class:error={errors.name}>
                  <input
                    type="text"
                    bind:value={form.name}
                    class="input input-lg"
                    placeholder="Goal name"
                  />
                  <div class="msg">{errors.name}</div>
                </div>
              </div>
              <div class="col-span-2" class:error={errors.assignee_id}>
                <label for="assignee_id" class="text-white text-lg">
                  Assignee
                </label>
                <div class="mt-1 input-cont" class:error={errors.assignee_id}>
                  <select
                    class="input input-lg"
                    id="goal_assignee_id"
                    bind:value={form.assignee_id}
                  >
                    {#if appStore.departments.length}
                      {#each appStore.departments as dept (dept.id)}
                        <option value={dept.id}>{dept.name}</option>
                      {/each}
                    {:else}
                      <option value="">No Departments</option>
                    {/if}
                  </select>
                  <div class="msg">{errors.assignee_id || ""}</div>
                </div>
              </div>

              <div class="col-span-2">
                <div class="flex items-center mb-1">
                  <AlignLeft class="text-gray-200 mr-2 h-4 w-4" />
                  <div class="text-white text-lg">
                    Description
                    <span class="text-gray-300 italic">(Optional)</span>
                  </div>
                </div>
                <Tiptap
                  content={form.description}
                  onupdate={({ html }) => (form.description = html)}
                  placeholder="Please add a description of what is going to be accomplished"
                  id="annual_description"
                  buttons={onlyTextButtons}
                />
              </div>
            </div>
          </div>
        </div>
      {/if}

      <PrevNextSkipButtons nextStepFn={nextStep} />
    {/if}
  </div>
</div>
<div
  class="relative w-full hidden lg:inline lg:absolute lg:inset-y-32 lg:-mb-32 rounded-tl-lg lg:-right-0 md:w-1/3 lg:w-1/2 lg:h-full bg-lwp-light-grey bg-right-bottom bg-no-repeat bg-contain"
  style="background-image: url({bg})"
>
  <img
    src={img}
    alt="Your Profile"
    class="absolute inset-0 inset-x-4 inset-y-4 xl:-inset-x-8 xl:inset-y-6 w-full object-fill rounded shadow-lg"
  />
</div>
